import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import { fileUpload, openNotification } from '../../../../../common/utils';
import LoaderComponent from '../../../../../components/LoaderComponent';
import { UPDATE_TOY } from '../graphql/Mutations';
import {
  GET_TOYS_SIGNED_URL,
  GET_TOY_VIDEO_SIGNED_PUT_URL,
  TOY_ADMIN,
} from '../graphql/Queries';
import {
  buildChecklistInput,
  buildRequiredContentsInput,
  mapChecklistToForm,
  mapRequiredContentsToForm,
} from '../toyContentUtils';
import ToyForm from './ToyForm';

const ToyEditContent = ({
  form,
  toyId,
  onSaved,
  onDirtyChange,
  onSubmittingChange,
}) => {
  const { dispatch } = useContext(AppContext);
  const [toyDetail, setToyDetail] = useState({});
  const [isToyLoading, setIsToyLoading] = useState(true);
  const [toyImages, setToyImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState([]);
  const [toyVideo, setToyVideo] = useState([]);

  const setIsSubmitLoading = (value) => {
    if (onSubmittingChange) {
      onSubmittingChange(value);
    }
  };

  const [getToysSignUrl] = useLazyQuery(GET_TOYS_SIGNED_URL, {
    fetchPolicy: 'network-only',
    onError() {
      setIsSubmitLoading(false);
    },
  });

  const [getToyVideoSignedPutUrl] = useLazyQuery(GET_TOY_VIDEO_SIGNED_PUT_URL, {
    fetchPolicy: 'network-only',
    onError() {
      setIsSubmitLoading(false);
    },
  });

  const [updateToy] = useMutation(UPDATE_TOY, {
    onError: () => {},
  });

  const [toyAdmin] = useLazyQuery(TOY_ADMIN, {
    onCompleted: (response) => {
      // uid keeps existing images identifiable for upload list & drag and drop
      const [featuredImageObj, ...toyImagesArray] = (
        response?.toyAdmin?.images || []
      ).map((image) => ({ ...image, uid: image?.key }));
      setToyImages(toyImagesArray);
      setFeaturedImage(featuredImageObj ? [featuredImageObj] : []);
      setToyVideo(
        response?.toyAdmin?.videoUrl
          ? { url: response?.toyAdmin?.videoUrl }
          : [],
      );
      setToyDetail({
        ...response?.toyAdmin,
        images: toyImagesArray,
        featuredImages: featuredImageObj ? [featuredImageObj] : [],
      });
      setIsToyLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    if (!toyId) return;
    setIsToyLoading(true);
    toyAdmin({
      variables: {
        where: {
          id: toyId,
        },
      },
    });
  }, [toyId]);

  const updateToyFun = async (values) => {
    setIsSubmitLoading(true);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
    const toyObj = {
      name: values?.name?.trim(),
      description: values?.description?.trim(),
      ageGroupIds: values?.ageGroup,
      categoryIds: values?.category,
      brandId: values?.brand,
      bagId: values?.packagingMaterial || null,
      materialId: values?.material,
      url: values?.url,
      shortDescription: values?.shortDescription?.trim(),
      eanNumber: values?.eanNumber?.trim(),
      price: parseFloat(values?.price),
      carbonEmission: parseFloat(values?.carbonEmission),
      toyNumber: values?.id?.trim(),
      facilitateIds: values?.facilitates,
      tagIds: values?.tags,
      genreIds: values?.genres,
      includedContentMasters: values?.includedContentMasters,
      requiredContents: buildRequiredContentsInput(values?.requiredContents),
      checklist: buildChecklistInput(values?.checklist),
      dimensions: values?.dimensions,
      type: values?.type,
      slug: values?.slug,
    };
    let images = [];
    let video = '';
    if (values?.video && !toyVideo?.url) {
      const { name } = values?.video?.[0];
      const ext = name?.substring(name?.lastIndexOf('.') + 1);
      const timestamp = Date?.now();
      const filename = name?.split('.')?.slice(0, -1)?.join('.');
      const newFilename = `${timestamp}_${filename}.${ext}`;
      const fileKey = `toy/${toyDetail?.imageUuid}/${newFilename}`;

      const res = await getToyVideoSignedPutUrl({
        variables: {
          data: {
            fileName: fileKey,
            imageUuid: toyDetail?.imageUuid,
          },
        },
      });
      if (res?.data) {
        try {
          try {
            await fileUpload(
              res?.data?.getToyVideoSignedPutUrl?.signedUrl,
              toyVideo?.[0]?.originFileObj,
            );
            video = res?.data?.getToyVideoSignedPutUrl?.fileName;
          } catch (error) {
            throw new Error(
              `${toyVideo?.name} upload failed. Please try again.`,
            );
          }
        } catch (error) {
          setIsSubmitLoading(false);
          openNotification('error', error.message);
        }
      }
    }

    if (values?.images) {
      // Featured image first, then images in their (possibly reordered) order
      const allImages = [
        ...(values?.featuredImages || []),
        ...(values?.images || []),
      ];
      const newImages = allImages?.filter((image) => !image?.url);
      let uploadedImages = [];

      if (newImages?.length) {
        const fileKeyArray = newImages?.map((item) => {
          const { name } = item;
          const ext = name?.substring(name?.lastIndexOf('.') + 1);
          const timestamp = Date?.now();
          const filename = name?.split('.')?.slice(0, -1)?.join('.');
          const newFilename = `${timestamp}_${filename}.${ext}`;
          const fileKey = `toy/${toyDetail?.imageUuid}/${newFilename}`;

          return fileKey;
        });

        const res = await getToysSignUrl({
          variables: {
            data: {
              fileNames: fileKeyArray,
              imageUuid: toyDetail?.imageUuid,
            },
          },
        });
        if (!res?.data) {
          setIsSubmitLoading(false);
          return;
        }

        try {
          uploadedImages = await Promise.all(
            res?.data?.getToyImagesSignedPutUrl?.map(
              async (signedData, index) => {
                try {
                  await fileUpload(
                    signedData?.signedUrl,
                    newImages?.[index]?.originFileObj,
                  );
                  return {
                    contentType: newImages?.[index]?.type,
                    key: signedData?.fileName,
                    size: `${newImages?.[index]?.size}`,
                  };
                } catch (error) {
                  throw new Error(
                    `${newImages?.[index]?.name} upload failed. Please try again.`,
                  );
                }
              },
            ),
          );
        } catch (error) {
          setIsSubmitLoading(false);
          openNotification('error', error.message);
          return;
        }
      }

      const pendingUploadedImages = [...uploadedImages];
      images = allImages?.map((image, index) => {
        const imageData = image?.url
          ? {
              contentType: image?.contentType,
              key: image?.key,
              size: image?.size,
            }
          : pendingUploadedImages?.shift();
        return { ...imageData, order: index + 1 };
      });
    }
    try {
      const response = await updateToy({
        variables: {
          where: {
            id: toyId,
          },
          data: {
            ...toyObj,
            images,
            videoKey: toyVideo?.url ? toyVideo?.[0]?.url : video,
          },
        },
      });
      if (response?.data) {
        if (onSaved) {
          onSaved();
        }
      }
    } catch (error) {
      setIsSubmitLoading(false);
      return error;
    }
    setIsSubmitLoading(false);
  };

  const handleShowPrompt = () => {
    if (onDirtyChange) {
      onDirtyChange(true);
    }
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: true,
    });
  };

  const initialValues = {
    ...toyDetail,
    category: toyDetail?.categories?.map((category) => category?.id),
    ageGroup: toyDetail?.ageGroups?.map((ageGroup) => ageGroup?.id),
    brand: toyDetail?.brand?.id,
    material: toyDetail?.material?.id,
    packagingMaterial: toyDetail?.bag?.id,
    eanNumber: toyDetail?.eanNumber,
    price: toyDetail?.price,
    id: toyDetail?.toyNumber,
    facilitates: toyDetail?.facilitates?.map((facilitate) => facilitate?.id),
    tags: toyDetail?.tags?.map((tag) => tag?.id),
    genres: toyDetail?.genres?.map((genre) => genre?.id),
    includedContentMasters: toyDetail?.includedContents?.map(
      (contentMaster) => ({
        id: contentMaster?.id,
        quantity: contentMaster?.quantity,
      }),
    ),
    requiredContents: mapRequiredContentsToForm(toyDetail?.requiredContents),
    checklist: mapChecklistToForm(toyDetail?.checklist),
    dimensions: toyDetail?.dimensions,
    type: toyDetail?.type,
    slug: toyDetail?.slug,
  };

  if (isToyLoading) {
    return (
      <LoaderComponent size="large" setHeight="60" spinning={isToyLoading} />
    );
  }

  return (
    <div className="toy-form">
      <ToyForm
        form={form}
        isEdit
        onFinish={updateToyFun}
        onSubmittingChange={onSubmittingChange}
        handleShowPrompt={handleShowPrompt}
        initialFormValues={initialValues}
        toyImages={toyImages}
        setToyImages={setToyImages}
        toyDetail={toyDetail}
        toyVideo={toyVideo}
        setToyVideo={setToyVideo}
        featuredImage={featuredImage}
        setFeaturedImage={setFeaturedImage}
      />
    </div>
  );
};

export default ToyEditContent;
